// @flow

import {
  type OpenedIncomingPayment,
  getHash,
  getCountry,
  getSafeAllowedPaymentTypes,
} from '@tectonics/prs/incoming-payment';
import { type LookAndFeel } from '@tectonics/look-and-feel';
import { type UserInputValues } from '@tectonics/prs/user-input-values';
import { type UserInputValidationErrors } from '@tectonics/prs/validation';
import { normalizeColombianPhoneNumber } from '@tectonics/commons/country/countries/colombia';

type CheckoutFailProps = {|
  incomingPayment: OpenedIncomingPayment,
  lookAndFeel: LookAndFeel,
  userInputValues: UserInputValues,
  userInputValidationResult: UserInputValidationErrors,
|};

export class CheckoutFail {
  props: CheckoutFailProps;

  constructor(props: CheckoutFailProps) {
    this.props = props;
  }

  asObject(): Object {
    const { incomingPayment, lookAndFeel, userInputValues, userInputValidationResult } = this.props;
    return {
      type: 'checkout_fail',
      project: 'tectonics',
      payload: {
        user_input_data: filterUserInputValues(userInputValues),
        user_input_validation: extractUserInputValidationErrorForAnalytics(userInputValidationResult),
        payment_data: {
          ...filterIncomingPayment(incomingPayment),
          look_and_feel_code: lookAndFeel.code,
        },
      },
    };
  }

  asJSON(): string {
    return JSON.stringify(this.asObject());
  }
}

function filterUserInputValues(userInputValues: UserInputValues): {[string]: any} {
  const filtered = { ...userInputValues };
  delete filtered['cardHolderName'];
  delete filtered['cardNumber'];
  delete filtered['cardExpiryDate'];
  delete filtered['cardSecurityCode'];
  if (filtered['colombianPhoneNumber']) {
    filtered['colombianPhoneNumber'] = normalizeColombianPhoneNumber(filtered['colombianPhoneNumber']);
  }
  return filtered;
}

function filterIncomingPayment(incomingPayment: OpenedIncomingPayment): {[string]: any} {
  return {
    hash: getHash(incomingPayment),
    country: getCountry(incomingPayment),
    payment_options: getSafeAllowedPaymentTypes(incomingPayment),
  };
}

function extractUserInputValidationErrorForAnalytics(userInputValidation: UserInputValidationErrors): {[string]: string} {
  const userInputValidationForAnalytics = {};
  Object.entries(userInputValidation)
    .forEach((item: any) => {
      const name = item[0];
      const validationError = item[1];
      if (validationError) {
        userInputValidationForAnalytics[name] = validationError.id;
      }
    });
  return userInputValidationForAnalytics;
}
